import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function Home({ onShowLogin }) {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [dailySignups, setDailySignups] = useState(0);
  const [currentMatchNotification, setCurrentMatchNotification] = useState(0);

  const testimonials = [
    {
      name: "Sarah M.",
      text: "I found genuine connections and lasting friendships through Flirteen. The quality of people here is exceptional.",
      role: "Marketing Manager",
      location: "New York"
    },
    {
      name: "Michael R.", 
      text: "After years away from dating, Flirteen helped me find meaningful relationships with people who share my values.",
      role: "Software Engineer",
      location: "San Francisco"
    },
    {
      name: "Jessica L.",
      text: "The platform's focus on real connections over superficial matching made all the difference in my experience.",
      role: "Healthcare Professional",
      location: "Chicago"
    },
    {
      name: "David K.",
      text: "I appreciate the mature community and the emphasis on building genuine relationships.",
      role: "Business Consultant",
      location: "London"
    }
  ];

  // Dynamic match notifications - changes every minute
  const matchNotifications = [
    { names: "Sarah & Mike", emoji: "🎉", action: "just matched!" },
    { names: "Emma & Alex", emoji: "💕", action: "started chatting!" },
    { names: "Lisa & David", emoji: "✨", action: "just connected!" },
    { names: "Maya & Ryan", emoji: "🌟", action: "found love!" },
    { names: "Zoe & James", emoji: "💖", action: "just matched!" },
    { names: "Anna & Chris", emoji: "🎊", action: "started dating!" },
    { names: "Kate & Tom", emoji: "💫", action: "just connected!" },
    { names: "Sophie & Ben", emoji: "🎉", action: "found their match!" },
    { names: "Mia & Luke", emoji: "💝", action: "started talking!" },
    { names: "Grace & Noah", emoji: "⭐", action: "just matched!" }
  ];

  const features = [
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor"/>
        </svg>
      ),
      title: "Meaningful Connections",
      description: "Connect with people who value depth over superficial interactions and build relationships that last."
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Diverse Community",
      description: "Meet professionals, creatives, and like-minded individuals from all walks of life and career stages."
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      title: "Privacy & Security",
      description: "Advanced verification and privacy controls keep your experience safe, secure, and authentic."
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 19c-5 0-8-3-8-8V9a8 8 0 0 1 8-8h6a8 8 0 0 1 8 8v2c0 5-3 8-8 8z" stroke="currentColor" strokeWidth="2"/>
          <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M19 13c0 1-1 1-1 1s-1 0-1-1 1-1 1-1 1 0 1 1" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      title: "Intelligent Matching",
      description: "Our sophisticated algorithm considers compatibility beyond surface-level preferences for better matches."
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      title: "Premium Experience",
      description: "Ad-free environment with advanced features designed for serious relationship seekers."
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <polygon points="16.24,7.76 14.12,14.12 7.76,16.24 9.88,9.88" fill="currentColor"/>
        </svg>
      ),
      title: "Global Reach",
      description: "Connect locally or expand your horizons with our growing international community of verified members."
    }
  ];

  // Generate daily signup count (changes once per day)
  useEffect(() => {
    const generateDailySignups = () => {
      const today = new Date().toDateString();
      const savedDate = localStorage.getItem('flirteen_signup_date');
      const savedCount = localStorage.getItem('flirteen_daily_signups');
      
      if (savedDate === today && savedCount) {
        setDailySignups(parseInt(savedCount));
      } else {
        // Generate random number between 250-450 for more realism
        const newCount = Math.floor(Math.random() * 200) + 250;
        setDailySignups(newCount);
        localStorage.setItem('flirteen_signup_date', today);
        localStorage.setItem('flirteen_daily_signups', newCount.toString());
      }
    };

    generateDailySignups();
  }, []);

  // Rotate match notifications every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMatchNotification((prev) => (prev + 1) % matchNotifications.length);
    }, 60000); // 60 seconds
    return () => clearInterval(interval);
  }, [matchNotifications.length]);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* SEO Meta Tags */}
      <head>
        <title>Flirteen - Premium Dating & Social Connection Platform</title>
        <meta name="description" content="Join Flirteen, the premium platform for meaningful connections. Meet verified professionals, build lasting relationships, and find genuine connections beyond superficial dating." />
        <meta name="keywords" content="dating app, professional dating, meaningful connections, relationship platform, verified profiles, premium dating, social networking, adult dating" />
        <meta property="og:title" content="Flirteen - Where Meaningful Connections Begin" />
        <meta property="og:description" content="Connect with verified professionals and build genuine relationships on the premium dating platform." />
        <meta property="og:image" content="/og-image.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://flirteen.com" />
      </head>
      
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
        position: 'relative'
      }}>
        {/* Navigation Header - Logo Only */}
        <div style={{
          position: 'absolute',
          top: 'clamp(10px, 3vh, 20px)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100
        }}>
          <img 
            src="/flirteen-logo.png" 
            alt="Flirteen Logo" 
            style={{ 
              height: 'clamp(80px, 15vw, 120px)',
              width: 'auto',
              filter: 'drop-shadow(0 6px 20px rgba(255, 107, 107, 0.4))',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.filter = 'drop-shadow(0 8px 30px rgba(255, 107, 107, 0.5))';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.filter = 'drop-shadow(0 6px 20px rgba(255, 107, 107, 0.4))';
            }}
          />
        </div>

        {/* Hero Section */}
        <section style={{ 
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          padding: 'clamp(140px, 18vh, 180px) clamp(15px, 4vw, 20px) clamp(40px, 8vh, 60px)',
          overflow: 'hidden'
        }}>

          {/* Enhanced Background with Hearts Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            overflow: 'hidden'
          }}>
            {/* Animated heart particles */}
            {[...Array(12)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                fontSize: `${Math.random() * 30 + 20}px`,
                color: `rgba(255, 107, 107, ${Math.random() * 0.3 + 0.1})`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `heartFloat${(i % 2) + 1} ${Math.random() * 3 + 4}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
                fontFamily: 'Apple Color Emoji, Segoe UI Emoji, system-ui'
              }}>
                {['💕', '💖', '💗', '💘', '💝', '💞'][Math.floor(Math.random() * 6)]}
              </div>
            ))}

            {/* Gradient mesh background */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 20%, rgba(255, 107, 107, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(0, 233, 255, 0.12) 0%, transparent 50%),
                radial-gradient(circle at 40% 60%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
              `,
              animation: 'backgroundPulse 8s ease-in-out infinite alternate'
            }} />
          </div>

          {/* Floating Success Indicators */}
          <div style={{
            position: 'absolute',
            top: '15%',
            right: '10%',
            background: 'rgba(34, 197, 94, 0.9)',
            padding: '12px 20px',
            borderRadius: '25px',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)',
            animation: 'slideDown 4s ease-in-out infinite',
            zIndex: 3,
            transition: 'all 0.5s ease'
          }}>
            {matchNotifications[currentMatchNotification].emoji} {matchNotifications[currentMatchNotification].names} {matchNotifications[currentMatchNotification].action}
          </div>

          <div style={{
            position: 'absolute',
            bottom: '25%',
            left: '8%',
            background: 'rgba(168, 85, 247, 0.9)',
            padding: '10px 18px',
            borderRadius: '20px',
            color: 'white',
            fontSize: '12px',
            fontWeight: '600',
            boxShadow: '0 6px 24px rgba(168, 85, 247, 0.4)',
            animation: 'slideDown 4s ease-in-out infinite 2s',
            zIndex: 3
          }}>
            💬 {Math.floor(Math.random() * 50) + 80} new messages today
          </div>

          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 2,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'clamp(30px, 8vw, 60px)',
            alignItems: 'center',
            padding: '0 clamp(15px, 4vw, 20px)'
          }}>
            {/* Left Column - Content */}
            <div>
              {/* Trust Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(34, 197, 94, 0.2)',
                border: '1px solid rgba(34, 197, 94, 0.4)',
                borderRadius: '25px',
                padding: '6px 16px',
                marginBottom: '20px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#22C55E'
              }}>
                <span style={{ fontSize: '16px' }}>✅</span>
                Trusted by 500K+ Singles Worldwide
              </div>

              <h1 style={{
                fontSize: 'clamp(2.4rem, 5vw, 4rem)',
                fontWeight: '900',
                lineHeight: '1.1',
                marginBottom: '24px',
                background: 'linear-gradient(135deg, #FF6B6B 0%, #00E9FF 50%, #8A2BE2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                letterSpacing: '-1px',
                textShadow: '0 0 60px rgba(255, 107, 107, 0.4)'
              }}>
                Find Your Perfect Match Today
              </h1>
              
              <p style={{
                fontSize: 'clamp(1.15rem, 2.8vw, 1.3rem)',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.5',
                marginBottom: '16px',
                fontWeight: '500',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}>
                Join the <strong style={{ color: '#FFD700' }}>premium dating platform</strong> where professionals find lasting love.
              </p>

              {/* Social Proof Stats */}
              <div style={{
                display: 'flex',
                gap: 'clamp(20px, 4vw, 30px)',
                marginBottom: '24px',
                flexWrap: 'wrap'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    background: 'linear-gradient(45deg, #FF6B6B, #FF8E8E)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px'
                  }}>💕</div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#FF6B6B' }}>2,847</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>matches this week</div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    background: 'linear-gradient(45deg, #00E9FF, #40E0FF)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px'
                  }}>⭐</div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#00E9FF' }}>4.9/5</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>user rating</div>
                  </div>
                </div>
              </div>

              {/* Premium Features Highlight */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.15), rgba(0, 233, 255, 0.1))',
                borderRadius: '15px',
                padding: 'clamp(15px, 4vw, 20px)',
                marginBottom: '28px',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    fontSize: '24px',
                    background: 'linear-gradient(45deg, #FFD700, #FF6B6B)',
                    borderRadius: '50%',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '40px',
                    height: '40px'
                  }}>💎</div>
                  <h3 style={{
                    fontSize: 'clamp(1.1rem, 2.8vw, 1.3rem)',
                    fontWeight: '700',
                    color: '#FFD700',
                    margin: 0,
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>Premium Benefits</h3>
                </div>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  fontSize: 'clamp(0.9rem, 2.2vw, 1rem)',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}>
                  <li style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#00E9FF' }}>✓</span> Unlimited matches and messages
                  </li>
                  <li style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#00E9FF' }}>✓</span> Virtual gifts and premium features
                  </li>
                  <li style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#FFD700' }}>⭐</span> <strong>Convert coins to Amazon gift cards!</strong>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#8A2BE2' }}>🎯</span> Advanced matching algorithms
                  </li>
                </ul>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                marginBottom: 'clamp(30px, 6vw, 40px)'
              }} className="hero-buttons-container">
                {/* Primary CTA - More compelling */}
                <button
                  onClick={onShowLogin}
                  style={{
                    padding: 'clamp(16px, 4vw, 20px) clamp(32px, 8vw, 48px)',
                    fontSize: 'clamp(1.1rem, 2.8vw, 1.25rem)',
                    fontWeight: '700',
                    background: 'linear-gradient(135deg, #FF6B6B, #00E9FF)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    boxShadow: '0 12px 48px rgba(255, 107, 107, 0.4)',
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%',
                    maxWidth: '320px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-4px) scale(1.02)';
                    e.target.style.boxShadow = '0 20px 60px rgba(255, 107, 107, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.boxShadow = '0 12px 48px rgba(255, 107, 107, 0.4)';
                  }}
                >
                  <span style={{ position: 'relative', zIndex: 2 }}>
                    🚀 Start Dating Free
                  </span>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                    animation: 'shine 3s infinite'
                  }} />
                </button>

                {/* Secondary CTA with urgency */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.8)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#22C55E',
                      animation: 'pulse 2s infinite'
                    }} />
                    <span>{dailySignups} people signed up today</span>
                  </div>
                  <button
                    onClick={() => document.getElementById('testimonials').scrollIntoView({ behavior: 'smooth' })}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#00E9FF',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  >
                    See success stories →
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div style={{
                display: 'flex',
                gap: 'clamp(20px, 6vw, 40px)',
                flexWrap: 'wrap',
                justifyContent: 'flex-start'
              }}>
                <div>
                  <div style={{ 
                    fontSize: 'clamp(1.6rem, 3vw, 1.8rem)', 
                    fontWeight: '700', 
                    color: '#00E9FF',
                    marginBottom: '4px',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    50K+
                  </div>
                  <div style={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    fontSize: 'clamp(0.8rem, 1.8vw, 0.85rem)',
                    fontWeight: '500',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    Verified Members
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: 'clamp(1.6rem, 3vw, 1.8rem)', 
                    fontWeight: '700', 
                    color: '#8A2BE2',
                    marginBottom: '4px',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    95%
                  </div>
                  <div style={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    fontSize: 'clamp(0.8rem, 1.8vw, 0.85rem)',
                    fontWeight: '500',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    Satisfaction Rate
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: 'clamp(1.6rem, 3vw, 1.8rem)', 
                    fontWeight: '700', 
                    color: '#FF6B6B',
                    marginBottom: '4px',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    1M+
                  </div>
                  <div style={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    fontSize: 'clamp(0.8rem, 1.8vw, 0.85rem)',
                    fontWeight: '500',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    Messages Exchanged
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Heart Icons Animation */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              height: 'clamp(400px, 50vh, 500px)',
              marginTop: 'clamp(0px, 2vh, 20px)'
            }} className="hero-hearts-section">
              {/* Main Container */}
              <div style={{
                width: '100%',
                maxWidth: '450px',
                height: '500px',
                position: 'relative',
                transform: `translateY(${scrollY * 0.1}px)`
              }}>
                
                {/* Central Large Heart */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '120px',
                  color: '#FF6B6B',
                  animation: 'heartBeat 2s ease-in-out infinite',
                  filter: 'drop-shadow(0 10px 30px rgba(255, 107, 107, 0.5))',
                  zIndex: 10
                }}>
                  ❤️
                </div>
                
                {/* Floating Hearts */}
                <div style={{
                  position: 'absolute',
                  top: '10%',
                  left: '15%',
                  fontSize: '48px',
                  color: '#FF8E8E',
                  animation: 'heartFloat1 4s ease-in-out infinite',
                  filter: 'drop-shadow(0 5px 15px rgba(255, 107, 107, 0.3))'
                }}>
                  💕
                </div>
                
                <div style={{
                  position: 'absolute',
                  top: '20%',
                  right: '10%',
                  fontSize: '36px',
                  color: '#FF6B9D',
                  animation: 'heartFloat2 3.5s ease-in-out infinite 0.5s',
                  filter: 'drop-shadow(0 5px 15px rgba(255, 107, 157, 0.3))'
                }}>
                  💖
                </div>
                
                <div style={{
                  position: 'absolute',
                  bottom: '25%',
                  left: '20%',
                  fontSize: '42px',
                  color: '#FF4757',
                  animation: 'heartFloat1 3s ease-in-out infinite 1s',
                  filter: 'drop-shadow(0 5px 15px rgba(255, 71, 87, 0.3))'
                }}>
                  💗
                </div>
                
                <div style={{
                  position: 'absolute',
                  bottom: '15%',
                  right: '25%',
                  fontSize: '38px',
                  color: '#FF3838',
                  animation: 'heartFloat2 4.5s ease-in-out infinite 1.5s',
                  filter: 'drop-shadow(0 5px 15px rgba(255, 56, 56, 0.3))'
                }}>
                  💘
                </div>
                
                <div style={{
                  position: 'absolute',
                  top: '35%',
                  left: '5%',
                  fontSize: '32px',
                  color: '#FF6B6B',
                  animation: 'heartFloat1 5s ease-in-out infinite 2s',
                  filter: 'drop-shadow(0 5px 15px rgba(255, 107, 107, 0.3))'
                }}>
                  💝
                </div>
                
                <div style={{
                  position: 'absolute',
                  top: '60%',
                  right: '8%',
                  fontSize: '40px',
                  color: '#E74C3C',
                  animation: 'heartFloat2 3.8s ease-in-out infinite 2.5s',
                  filter: 'drop-shadow(0 5px 15px rgba(231, 76, 60, 0.3))'
                }}>
                  💞
                </div>
                
                <div style={{
                  position: 'absolute',
                  bottom: '35%',
                  right: '5%',
                  fontSize: '35px',
                  color: '#FF6B8A',
                  animation: 'heartFloat1 4.2s ease-in-out infinite 3s',
                  filter: 'drop-shadow(0 5px 15px rgba(255, 107, 138, 0.3))'
                }}>
                  💟
                </div>
                
                {/* Heart Particles */}
                <div style={{
                  position: 'absolute',
                  top: '40%',
                  left: '60%',
                  fontSize: '24px',
                  color: '#FF8A8A',
                  animation: 'heartParticle 6s ease-in-out infinite',
                  filter: 'drop-shadow(0 3px 10px rgba(255, 138, 138, 0.3))'
                }}>
                  ❤️
                </div>
                
                <div style={{
                  position: 'absolute',
                  top: '25%',
                  left: '40%',
                  fontSize: '20px',
                  color: '#FF7F7F',
                  animation: 'heartParticle 5s ease-in-out infinite 1s',
                  filter: 'drop-shadow(0 3px 10px rgba(255, 127, 127, 0.3))'
                }}>
                  💕
                </div>
                
                <div style={{
                  position: 'absolute',
                  top: '70%',
                  left: '35%',
                  fontSize: '22px',
                  color: '#FF9999',
                  animation: 'heartParticle 4.5s ease-in-out infinite 2s',
                  filter: 'drop-shadow(0 3px 10px rgba(255, 153, 153, 0.3))'
                }}>
                  💖
                </div>
                
                {/* Connection Success Indicator */}
                <div style={{
                  position: 'absolute',
                  top: '10%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(255, 107, 107, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: '8px 16px',
                  border: '1px solid rgba(255, 107, 107, 0.3)',
                  color: '#FF6B6B',
                  fontSize: '12px',
                  fontWeight: '600',
                  animation: 'slideDown 3s ease-in-out infinite'
                }}>
                  💕 Find Love Here 💕
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Premium Rewards Section */}
        <section style={{
          padding: 'clamp(60px, 10vh, 80px) clamp(15px, 4vw, 20px)',
          background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 107, 107, 0.08) 50%, rgba(0, 233, 255, 0.06) 100%)',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '50px' }}>
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 2.8rem)',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #FFD700 0%, #FF6B6B 50%, #8A2BE2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '15px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}>
                💎 Premium Rewards Program
              </h2>
              <p style={{
                fontSize: 'clamp(1.1rem, 2.4vw, 1.2rem)',
                color: 'rgba(255, 255, 255, 0.9)',
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: '1.6',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}>
                Earn coins through activity and convert them to real Amazon gift cards!
              </p>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 107, 107, 0.15))',
              borderRadius: 'clamp(20px, 5vw, 30px)',
              padding: 'clamp(25px, 6vw, 40px)',
              border: '2px solid rgba(255, 215, 0, 0.4)',
              marginBottom: '40px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Background decoration */}
              <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                fontSize: '120px',
                opacity: '0.1',
                color: '#FFD700'
              }}>🎁</div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 'clamp(25px, 5vw, 35px)',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      fontSize: '48px',
                      background: 'linear-gradient(45deg, #FFD700, #FF6B6B)',
                      borderRadius: '20px',
                      padding: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 10px 30px rgba(255, 215, 0, 0.3)'
                    }}>💰</div>
                    <div>
                      <h3 style={{
                        fontSize: 'clamp(1.3rem, 3vw, 1.6rem)',
                        fontWeight: '700',
                        color: '#FFD700',
                        margin: '0 0 5px 0',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                      }}>Coin to Gift Card</h3>
                      <p style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: 'clamp(0.9rem, 2.2vw, 1rem)',
                        margin: 0
                      }}>Convert your earned coins</p>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    fontSize: 'clamp(0.95rem, 2.3vw, 1.05rem)',
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#FFD700' }}>⭐</span>
                      <span>Earn coins through daily activities</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#00E9FF' }}>💎</span>
                      <span>Premium members get bonus coins</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#FF6B6B' }}>🎁</span>
                      <span>Redeem for Amazon gift cards</span>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #FFD700, #FF6B6B)',
                    borderRadius: '20px',
                    padding: '25px',
                    color: 'white',
                    fontWeight: '700',
                    marginBottom: '20px',
                    boxShadow: '0 15px 40px rgba(255, 215, 0, 0.4)'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🏆</div>
                    <div style={{ fontSize: '1.8rem', marginBottom: '5px' }}>5000 Coins</div>
                    <div style={{ fontSize: '1.2rem', opacity: 0.9 }}>= $10 Amazon Gift Card</div>
                  </div>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.9rem',
                    fontStyle: 'italic'
                  }}>
                    Start earning coins today!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" style={{
          padding: 'clamp(60px, 12vh, 100px) clamp(15px, 4vw, 20px)',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '80px' }}>
              <h2 style={{
                fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)',
                fontWeight: '700',
                background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '18px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}>
                Why Choose Flirteen
              </h2>
              <p style={{
                fontSize: 'clamp(1.05rem, 2.2vw, 1.15rem)',
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: '1.6',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}>
                Designed for professionals and mature individuals seeking genuine connections and meaningful relationships.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'clamp(20px, 5vw, 40px)'
            }}>
              {features.map((feature, index) => (
                <div key={index} style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 'clamp(15px, 4vw, 25px)',
                  padding: 'clamp(25px, 6vw, 40px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 233, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(0, 233, 255, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}>
                  <div style={{
                    color: '#00E9FF',
                    marginBottom: '20px',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    {feature.icon}
                  </div>
                  <h3 style={{
                    fontSize: 'clamp(1.25rem, 2.8vw, 1.4rem)',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '12px',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    fontSize: 'clamp(0.95rem, 2.1vw, 1.05rem)',
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: '1.6',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories Section */}
        <section id="testimonials" style={{
          padding: 'clamp(80px, 15vh, 120px) clamp(15px, 4vw, 20px)',
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(15, 15, 35, 0.5) 100%)',
          position: 'relative',
          zIndex: 2,
          overflow: 'hidden'
        }}>
          {/* Background Hearts */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}>
            <div style={{
              position: 'absolute',
              top: '10%',
              left: '10%',
              fontSize: '60px',
              color: 'rgba(255, 107, 107, 0.1)',
              animation: 'heartFloat1 8s ease-in-out infinite',
              transform: `translateY(${scrollY * -0.1}px)`
            }}>💕</div>
            
            <div style={{
              position: 'absolute',
              top: '20%',
              right: '15%',
              fontSize: '45px',
              color: 'rgba(255, 107, 107, 0.08)',
              animation: 'heartFloat2 6s ease-in-out infinite 1s',
              transform: `translateY(${scrollY * 0.08}px)`
            }}>💖</div>
            
            <div style={{
              position: 'absolute',
              bottom: '15%',
              left: '20%',
              fontSize: '50px',
              color: 'rgba(255, 107, 107, 0.12)',
              animation: 'heartFloat1 7s ease-in-out infinite 2s',
              transform: `translateY(${scrollY * -0.05}px)`
            }}>💗</div>
          </div>
          
          <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative' }}>
            <div style={{ textAlign: 'center', marginBottom: '80px' }}>
              <h2 style={{
                fontSize: 'clamp(2.1rem, 4.2vw, 3.1rem)',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #FF6B6B 0%, #00E9FF 50%, #8A2BE2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '18px',
                letterSpacing: '-0.8px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}>
                Success Story
              </h2>
              <p style={{
                fontSize: 'clamp(1.05rem, 2.2vw, 1.15rem)',
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: '600px',
                margin: '0 auto',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}>
                Real love story from verified professionals who found their perfect match
              </p>
            </div>

            {/* Single Featured Testimonial */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.15), rgba(0, 233, 255, 0.08), rgba(138, 43, 226, 0.12))',
              backdropFilter: 'blur(20px)',
              borderRadius: 'clamp(20px, 5vw, 30px)',
              padding: 'clamp(30px, 8vw, 60px) clamp(25px, 6vw, 50px)',
              border: '2px solid rgba(255, 107, 107, 0.3)',
              textAlign: 'center',
              position: 'relative',
              boxShadow: '0 30px 80px rgba(255, 107, 107, 0.25)',
              animation: 'testimonialGlow 3s ease-in-out infinite alternate'
            }}>
              {/* Decorative Hearts */}
              <div style={{
                position: 'absolute',
                top: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '40px',
                animation: 'heartBeat 2s ease-in-out infinite'
              }}>💕</div>
              
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                fontSize: '30px',
                color: 'rgba(255, 107, 107, 0.6)',
                animation: 'heartFloat1 4s ease-in-out infinite'
              }}>💖</div>
              
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                fontSize: '25px',
                color: 'rgba(255, 107, 107, 0.5)',
                animation: 'heartFloat2 5s ease-in-out infinite 1s'
              }}>💗</div>
              
              {/* Quote */}
              <div style={{
                fontSize: '80px',
                color: 'rgba(255, 107, 107, 0.3)',
                marginBottom: '30px',
                fontFamily: 'serif',
                lineHeight: '1'
              }}>
                "
              </div>
              
              <p style={{
                fontSize: 'clamp(1.15rem, 2.6vw, 1.25rem)',
                color: 'rgba(255, 255, 255, 0.95)',
                lineHeight: '1.7',
                marginBottom: '35px',
                fontStyle: 'italic',
                maxWidth: '700px',
                margin: '0 auto 35px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}>
                "I found genuine connections and lasting friendships through Flirteen. The quality of people here is exceptional. After months of meaningless swipes on other apps, Flirteen helped me find someone who truly understands me."
              </p>
              
              {/* User Profile */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '20px',
                marginTop: '50px',
                paddingTop: '30px',
                borderTop: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                {/* Profile Photos */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <div style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '24px',
                    border: '3px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 10px 30px rgba(255, 107, 107, 0.3)'
                  }}>
                    S
                  </div>
                  
                  <div style={{
                    fontSize: '30px',
                    color: '#FF6B6B',
                    animation: 'heartBeat 2s ease-in-out infinite 0.5s'
                  }}>💕</div>
                  
                  <div style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #00E9FF, #40E0FF)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '24px',
                    border: '3px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 10px 30px rgba(0, 233, 255, 0.3)'
                  }}>
                    M
                  </div>
                </div>
              </div>
              
              <div style={{ marginTop: '25px' }}>
                <h4 style={{
                  fontSize: 'clamp(1.15rem, 2.4vw, 1.2rem)',
                  color: '#FF6B6B',
                  fontWeight: '600',
                  margin: 0,
                  marginBottom: '6px',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}>
                  Sarah & Michael
                </h4>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: 'clamp(0.9rem, 1.9vw, 0.95rem)',
                  margin: 0,
                  marginBottom: '8px',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}>
                  Marketing Manager & Software Engineer
                </p>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: 'clamp(0.8rem, 1.7vw, 0.85rem)',
                  margin: 0,
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}>
                  New York • Together for 2 years 💕
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section style={{
          padding: 'clamp(80px, 15vh, 120px) clamp(15px, 4vw, 20px)',
          background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.08) 0%, rgba(0, 233, 255, 0.05) 50%, rgba(138, 43, 226, 0.08) 100%)',
          position: 'relative',
          zIndex: 2,
          overflow: 'hidden'
        }}>
          {/* Floating Hearts Background */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1
          }}>
            {/* Large Floating Hearts */}
            <div style={{
              position: 'absolute',
              top: '10%',
              left: '10%',
              fontSize: '80px',
              color: 'rgba(255, 107, 107, 0.15)',
              animation: 'heartFloat1 10s ease-in-out infinite',
              transform: `translateY(${scrollY * -0.1}px)`
            }}>💕</div>
            
            <div style={{
              position: 'absolute',
              top: '15%',
              right: '8%',
              fontSize: '60px',
              color: 'rgba(255, 107, 107, 0.12)',
              animation: 'heartFloat2 8s ease-in-out infinite 1s',
              transform: `translateY(${scrollY * 0.08}px)`
            }}>💖</div>
            
            <div style={{
              position: 'absolute',
              bottom: '20%',
              left: '15%',
              fontSize: '70px',
              color: 'rgba(255, 107, 107, 0.1)',
              animation: 'heartFloat1 12s ease-in-out infinite 2s',
              transform: `translateY(${scrollY * 0.05}px)`
            }}>💗</div>
            
            <div style={{
              position: 'absolute',
              bottom: '10%',
              right: '20%',
              fontSize: '55px',
              color: 'rgba(255, 107, 107, 0.18)',
              animation: 'heartFloat2 9s ease-in-out infinite 3s',
              transform: `translateY(${scrollY * -0.08}px)`
            }}>💘</div>
            
            {/* Medium Hearts */}
            <div style={{
              position: 'absolute',
              top: '40%',
              left: '5%',
              fontSize: '45px',
              color: 'rgba(255, 107, 107, 0.2)',
              animation: 'heartParticle 7s ease-in-out infinite',
              transform: `translateY(${scrollY * 0.12}px)`
            }}>💝</div>
            
            <div style={{
              position: 'absolute',
              top: '60%',
              right: '5%',
              fontSize: '40px',
              color: 'rgba(255, 107, 107, 0.16)',
              animation: 'heartParticle 6s ease-in-out infinite 1.5s',
              transform: `translateY(${scrollY * -0.06}px)`
            }}>💞</div>
            
            <div style={{
              position: 'absolute',
              top: '25%',
              left: '85%',
              fontSize: '35px',
              color: 'rgba(255, 107, 107, 0.14)',
              animation: 'heartFloat1 11s ease-in-out infinite 4s',
              transform: `translateY(${scrollY * 0.09}px)`
            }}>💟</div>
            
            {/* Small Heart Particles */}
            <div style={{
              position: 'absolute',
              top: '30%',
              left: '25%',
              fontSize: '25px',
              color: 'rgba(255, 107, 107, 0.25)',
              animation: 'heartParticle 5s ease-in-out infinite 2.5s',
              transform: `translateY(${scrollY * -0.04}px)`
            }}>❤️</div>
            
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '70%',
              fontSize: '30px',
              color: 'rgba(255, 107, 107, 0.22)',
              animation: 'heartFloat2 8.5s ease-in-out infinite 1.8s',
              transform: `translateY(${scrollY * 0.07}px)`
            }}>💕</div>
            
            <div style={{
              position: 'absolute',
              bottom: '35%',
              left: '40%',
              fontSize: '28px',
              color: 'rgba(255, 107, 107, 0.19)',
              animation: 'heartParticle 6.5s ease-in-out infinite 3.2s',
              transform: `translateY(${scrollY * -0.03}px)`
            }}>💖</div>
          </div>

          <div style={{ 
            maxWidth: '900px', 
            margin: '0 auto', 
            textAlign: 'center',
            position: 'relative',
            zIndex: 2
          }}>
            <h2 style={{
              fontSize: 'clamp(2.2rem, 4.4vw, 3.6rem)',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #FF6B6B 0%, #00E9FF 50%, #8A2BE2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '26px',
              letterSpacing: '-0.8px',
              animation: 'textGlow 3s ease-in-out infinite alternate',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            }}>
              Ready to Connect?
            </h2>
            
            <p style={{
              fontSize: 'clamp(1.2rem, 2.8vw, 1.3rem)',
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '45px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '0 auto 45px',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            }}>
              Join thousands of verified professionals who have found meaningful connections. 
              Your perfect match is waiting to meet someone just like you.
            </p>
            
            {/* CTA Button with surrounding elements */}
            <div style={{
              position: 'relative',
              display: 'inline-block',
              marginBottom: '40px'
            }}>
              {/* Background glow effect */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '300px',
                height: '300px',
                background: 'radial-gradient(circle, rgba(255, 107, 107, 0.2) 0%, transparent 70%)',
                borderRadius: '50%',
                animation: 'pulse 3s ease-in-out infinite',
                zIndex: -1
              }} />
              
              <button
                onClick={onShowLogin}
                style={{
                  padding: 'clamp(16px, 4vw, 24px) clamp(40px, 12vw, 80px)',
                  fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #FF6B6B, #00E9FF, #8A2BE2)',
                  backgroundSize: '200% 200%',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'clamp(40px, 8vw, 60px)',
                  cursor: 'pointer',
                  boxShadow: '0 20px 60px rgba(255, 107, 107, 0.4)',
                  transition: 'all 0.4s ease',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  position: 'relative',
                  overflow: 'hidden',
                  animation: 'gradientShift 3s ease infinite',
                  minHeight: '56px',
                  minWidth: '200px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-8px) scale(1.08)';
                  e.target.style.boxShadow = '0 30px 80px rgba(255, 107, 107, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 20px 60px rgba(255, 107, 107, 0.4)';
                }}
              >
                <span style={{
                  position: 'relative',
                  zIndex: 2
                }}>
                  Start Your Journey Today
                </span>
                
                {/* Button shine effect */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  animation: 'shine 3s infinite',
                  zIndex: 1
                }} />
              </button>
            </div>

            {/* Features list */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 'clamp(20px, 5vw, 40px)',
              flexWrap: 'wrap',
              marginBottom: 'clamp(20px, 4vw, 30px)'
            }}>
              {[
                { text: 'Free to join', icon: '✓' },
                { text: 'Verified profiles', icon: '✓' },
                { text: 'Premium experience', icon: '✓' },
                { text: 'Secure & private', icon: '✓' }
              ].map((feature, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
                  fontWeight: '500',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}>
                  <span style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #00E9FF, #8A2BE2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '700'
                  }}>
                    {feature.icon}
                  </span>
                  {feature.text}
                </div>
              ))}
            </div>

            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
              margin: 0,
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            }}>
              Join over 50,000 verified professionals finding meaningful connections
            </p>
          </div>
        </section>

        {/* CSS Animations and Fonts */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
          
          @keyframes heartBeat {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.1);
            }
          }

          @keyframes heartFloat1 {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            25% {
              transform: translateY(-15px) rotate(5deg);
            }
            50% {
              transform: translateY(-30px) rotate(0deg);
            }
            75% {
              transform: translateY(-10px) rotate(-5deg);
            }
          }

          @keyframes heartFloat2 {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            33% {
              transform: translateY(-20px) rotate(-8deg);
            }
            66% {
              transform: translateY(-25px) rotate(8deg);
            }
          }

          @keyframes heartParticle {
            0% {
              transform: translateY(0px) scale(0.8);
              opacity: 0.4;
            }
            50% {
              transform: translateY(-40px) scale(1.2);
              opacity: 1;
            }
            100% {
              transform: translateY(-80px) scale(0.6);
              opacity: 0;
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(180deg);
            }
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes textGlow {
            0% {
              filter: drop-shadow(0 0 20px rgba(255, 107, 107, 0.4));
            }
            50% {
              filter: drop-shadow(0 0 40px rgba(0, 233, 255, 0.6));
            }
            100% {
              filter: drop-shadow(0 0 20px rgba(138, 43, 226, 0.4));
            }
          }

          @keyframes morph {
            0%, 100% {
              border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
            }
            25% {
              border-radius: 58% 42% 75% 25% / 76% 24% 76% 24%;
            }
            50% {
              border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%;
            }
            75% {
              border-radius: 33% 67% 58% 42% / 63% 68% 32% 37%;
            }
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.8;
            }
            50% {
              transform: scale(1.05);
              opacity: 1;
            }
          }

          @keyframes logoFloat {
            0%, 100% {
              transform: translate(-50%, -50%) translateY(0px);
            }
            50% {
              transform: translate(-50%, -50%) translateY(-10px);
            }
          }

          @keyframes nodeFloat {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-15px);
            }
          }

          @keyframes pathDraw {
            0% {
              stroke-dashoffset: 100;
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              stroke-dashoffset: -100;
              opacity: 0;
            }
          }

          @keyframes barGrow {
            0%, 100% {
              transform: scaleY(0.8);
            }
            50% {
              transform: scaleY(1.2);
            }
          }

          @keyframes slideDown {
            0% {
              transform: translateX(-50%) translateY(-20px);
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              transform: translateX(-50%) translateY(10px);
              opacity: 0;
            }
          }

          @keyframes gridMove {
            0% {
              transform: translate(0, 0);
            }
            100% {
              transform: translate(30px, 30px);
            }
          }

          @keyframes testimonialGlow {
            0% {
              box-shadow: 0 20px 60px rgba(0, 233, 255, 0.2);
            }
            100% {
              box-shadow: 0 20px 60px rgba(255, 107, 107, 0.3);
            }
          }

          @keyframes backgroundShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          @keyframes gradientShift {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }

          @keyframes shine {
            0% {
              left: -100%;
            }
            50%, 100% {
              left: 100%;
            }
          }

          @keyframes backgroundPulse {
            0% {
              opacity: 0.6;
              transform: scale(1);
            }
            100% {
              opacity: 0.8;
              transform: scale(1.05);
            }
          }

          @media (max-width: 768px) {
            /* Mobile responsive styles */
            div[style*="grid-template-columns: repeat(auto-fit, minmax(300px, 1fr))"] {
              grid-template-columns: 1fr !important;
              gap: clamp(20px, 6vw, 40px) !important;
              text-align: center !important;
            }
            
            /* Mobile grid optimizations for rewards section */
            div[style*="grid-template-columns: repeat(auto-fit, minmax(280px, 1fr))"] {
              grid-template-columns: 1fr !important;
              gap: clamp(20px, 5vw, 25px) !important;
              text-align: center !important;
            }
            
            /* Hide complex background animations on mobile for performance */
            div[style*="position: absolute"][style*="animation"][style*="background"] {
              display: none !important;
            }
            
            /* Hide parallax background elements on mobile */
            div[style*="position: absolute"][style*="transform: translateY"] {
              display: none !important;
            }
            
            /* Right column hearts section - significantly reduce height on mobile */
            .hero-hearts-section {
              height: clamp(150px, 25vh, 200px) !important;
              margin-bottom: 15px !important;
              margin-top: 0px !important;
            }
            
            /* Show only main heart on mobile, hide floating ones */
            div[style*="fontSize: '120px'"] {
              font-size: 60px !important;
            }
            
            /* Hide smaller floating hearts on mobile */
            div[style*="position: absolute"][style*="fontSize: '48px'"],
            div[style*="position: absolute"][style*="fontSize: '36px'"],
            div[style*="position: absolute"][style*="fontSize: '42px'"],
            div[style*="position: absolute"][style*="fontSize: '38px'"],
            div[style*="position: absolute"][style*="fontSize: '32px'"],
            div[style*="position: absolute"][style*="fontSize: '40px'"],
            div[style*="position: absolute"][style*="fontSize: '35px'"],
            div[style*="position: absolute"][style*="fontSize: '24px'"],
            div[style*="position: absolute"][style*="fontSize: '20px'"],
            div[style*="position: absolute"][style*="fontSize: '22px'"] {
              display: none !important;
            }
            
            /* Show only the central success indicator */
            div[style*="Find Love Here"] {
              display: block !important;
              position: relative !important;
              top: auto !important;
              left: auto !important;
              transform: none !important;
              margin-bottom: 15px !important;
            }
            
            /* CTA section improvements */
            div[style*="maxWidth: '900px'"] {
              padding: 0 15px !important;
            }
            
            /* Hero buttons mobile alignment */
            .hero-buttons-container {
              justify-content: center !important;
              gap: 15px !important;
              width: 100% !important;
            }
            
            .hero-buttons-container button {
              width: 100% !important;
              max-width: 280px !important;
              margin: 0 !important;
              flex: 1 1 auto !important;
            }

            /* Mobile-optimized premium highlights box */
            div[style*="background: linear-gradient(135deg, rgba(255, 107, 107, 0.15)"] {
              margin: 15px 0 !important;
              padding: 15px !important;
            }

            /* Mobile font size optimization for premium benefits */
            div[style*="Premium Benefits"] h3 {
              font-size: 1.1rem !important;
            }

            /* Rewards section mobile optimizations */
            div[style*="background: linear-gradient(135deg, rgba(255, 215, 0, 0.2)"] {
              padding: 20px !important;
              margin-bottom: 25px !important;
            }

            /* Coin conversion card mobile sizing */
            div[style*="background: linear-gradient(135deg, #FFD700, #FF6B6B)"] {
              padding: 20px !important;
              margin-bottom: 15px !important;
            }

            /* Mobile icon sizing for rewards */
            div[style*="fontSize: '48px'"][style*="padding: '15px'"] {
              font-size: 36px !important;
              padding: 12px !important;
            }
          }

          @media (max-width: 480px) {
            /* Extra small mobile adjustments */
            div[style*="height: clamp(200px, 30vh, 250px)"] {
              height: clamp(150px, 25vh, 180px) !important;
            }
            
            /* Further reduce main heart size on very small screens */
            div[style*="fontSize: '60px'"] {
              font-size: 45px !important;
            }
            
            /* Ensure text is readable on small screens */
            p[style*="fontSize: '1.25rem'"] {
              font-size: 1.1rem !important;
              line-height: 1.5 !important;
            }
            
            /* Stack stats vertically on very small screens */
            div[style*="justifyContent: 'flex-start'"] {
              justify-content: center !important;
            }
            
            /* Further reduce hero section padding on very small screens */
            section[style*="padding: clamp(100px, 12vh, 130px)"] {
              padding-top: clamp(90px, 10vh, 110px) !important;
            }
            
            /* Ensure buttons stack vertically on very small screens */
            .hero-buttons-container {
              flex-direction: column !important;
              align-items: center !important;
              gap: 12px !important;
            }
            
            .hero-buttons-container button {
              max-width: 250px !important;
            }
          }
          
          @media (max-width: 360px) {
            /* Extra small screen optimizations */
            div[style*="minWidth: '200px'"] {
              min-width: 150px !important;
            }
            
            button[style*="minWidth: '200px'"] {
              min-width: 160px !important;
              padding: 12px 20px !important;
            }
          }
        `}</style>
      </div>
    </>
  );
}

export default Home;